import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; 
import { FormFieldDefinition, IntakeFormTemplate, FormFieldType } from '../../../types/formBuilder';
import { createForm, updateForm, getFormById } from '../../../firebase/formManagement';
import { User } from '../../../types';
import { QuestionBank } from './QuestionBank';
import { QuestionBankSidebar } from './QuestionBankSidebar';
import { QuestionBankItem } from '../../../utils/defaultFormsSetup';

interface FormBuilderProps {
  user: User;
}

export function FormBuilder({ user }: FormBuilderProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { formId } = useParams<{ formId?: string }>();
  
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [fields, setFields] = useState<FormFieldDefinition[]>([]);
  const [editingField, setEditingField] = useState<FormFieldDefinition | null>(null);
  const [showFieldEditor, setShowFieldEditor] = useState(false);
  const [showQuestionBank, setShowQuestionBank] = useState(true);
  const [draggedQuestion, setDraggedQuestion] = useState<QuestionBankItem | null>(null);
  const [draggedFieldIndex, setDraggedFieldIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sharedWith, setSharedWith] = useState<'all' | 'specific'>('all');
  const allRoles = ['admin', 'department_manager', 'program_manager', 'team_manager', 'instructor', 'social_worker'];
  const [allowedRoles, setAllowedRoles] = useState<string[]>(allRoles);
  const [showSettings, setShowSettings] = useState(false);

  // Load existing form if editing
  useEffect(() => {
    if (formId) {
      loadForm();
    }
  }, [formId]);

  const loadForm = async () => {
    if (!formId) return;
    
    setIsLoading(true);
    try {
      const form = await getFormById(formId);
      if (form) {
        setFormName(form.name || '');
        setFormDescription(form.description || '');
        setFields(form.fields || []);
        setSharedWith(form.sharedWith || 'all');
        // If sharedWith is 'all', set all roles; otherwise use the saved roles
        if (form.sharedWith === 'all') {
          setAllowedRoles(allRoles);
        } else {
          setAllowedRoles(form.allowedRoles || []);
        }
      }
    } catch (error) {
      console.error('Error loading form:', error);
      alert(t('formBuilder.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddField = () => {
    const newField: FormFieldDefinition = {
      id: `field_${Date.now()}`,
      field_name: '',
      label: '',
      type: 'text',
      required: false,
      order: fields.length
    };
    setEditingField(newField);
    setShowFieldEditor(true);
  };

  const handleAddFromQuestionBank = (question: QuestionBankItem, index?: number) => {
    const newField: FormFieldDefinition = {
      id: `field_${Date.now()}`,
      field_name: question.field_name,
      label: question.labelHe,
      type: question.type as FormFieldType,
      options: question.options,
      description: question.description,
      required: false,
      order: index !== undefined ? index : fields.length
    };
    
    if (index !== undefined && index >= 0 && index <= fields.length) {
      const newFields = [...fields];
      newFields.splice(index, 0, newField);
      // Update order for all fields
      newFields.forEach((field, idx) => {
        field.order = idx;
      });
      setFields(newFields);
    } else {
      setFields([...fields, newField]);
    }
  };

  const handleEditField = (field: FormFieldDefinition) => {
    setEditingField({ ...field });
    setShowFieldEditor(true);
  };

  const handleSaveField = () => {
    if (!editingField || !editingField.label) {
      alert('Field label is required');
      return;
    }
    // Ensure field_name is set (auto-generated from label if missing)
    if (!editingField.field_name && editingField.label) {
      const fieldName = editingField.label
        .toLowerCase()
        .replace(/[^\w\s]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
      editingField.field_name = fieldName || `field_${Date.now()}`;
    }

    const existingIndex = fields.findIndex(f => f.id === editingField.id);
    if (existingIndex >= 0) {
      // Update existing field
      const updatedFields = [...fields];
      updatedFields[existingIndex] = editingField;
      setFields(updatedFields);
    } else {
      // Add new field
      setFields([...fields, editingField]);
    }

    setShowFieldEditor(false);
    setEditingField(null);
  };

  const handleDeleteField = (fieldId: string) => {
    setFields(fields.filter(f => f.id !== fieldId));
  };

  const handleMoveField = (index: number, direction: 'up' | 'down') => {
    const newFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newFields.length) return;
    
    [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
    
    // Update order property
    newFields.forEach((field, idx) => {
      field.order = idx;
    });
    
    setFields(newFields);
  };

  const handleSaveForm = async () => {
    if (!formName.trim()) {
      alert(t('formBuilder.nameRequired'));
      return;
    }

    if (fields.length === 0) {
      alert(t('formBuilder.addAtLeastOneField'));
      return;
    }

    if (sharedWith === 'specific' && allowedRoles.length === 0) {
      alert(t('formBuilder.selectAtLeastOneRole') || 'Please select at least one role');
      return;
    }

    setIsSaving(true);
    try {
      const formData: Omit<IntakeFormTemplate, 'id' | 'createdAt' | 'updatedAt' | 'version'> = {
        name: formName,
        nameHe: formName,
        nameEn: formName,
        description: formDescription,
        type: 'custom',
        fields,
        isActive: true,
        isDefault: false,
        createdBy: user.email,
        sharedWith,
        allowedRoles: sharedWith === 'all' ? allRoles : allowedRoles
      };

      let result;
      if (formId) {
        // Update existing form
        result = await updateForm(formId, formData, user.email, user.role);
      } else {
        // Create new form
        result = await createForm(formData, user.email, user.role);
      }

      if (result.success) {
        alert(formId ? t('formBuilder.updateSuccess') : t('formBuilder.createSuccess'));
        navigate('/dashboard?tab=forms');
      } else {
        alert(result.error || t('formBuilder.saveFailed'));
      }
    } catch (error) {
      console.error('Error saving form:', error);
      alert(t('formBuilder.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e: React.DragEvent, index?: number) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const questionData = e.dataTransfer.getData('text/plain');
      const question: QuestionBankItem = JSON.parse(questionData);
      // Only add if it's a question from the bank (not a field reorder)
      if (!question.type || question.field_name) {
        handleAddFromQuestionBank(question, index);
      }
    } catch (error) {
      console.error('Error dropping question:', error);
    }
    // Reset drag state
    setDragOverIndex(null);
  };

  const handleFieldDragStart = (e: React.DragEvent, index: number) => {
    setDraggedFieldIndex(index);
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'field', index }));
    e.dataTransfer.effectAllowed = 'move';
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleFieldDragEnd = (e: React.DragEvent) => {
    setDraggedFieldIndex(null);
    setDragOverIndex(null);
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
  };

  const handleFieldDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const data = e.dataTransfer.getData('text/plain');
      const parsed = JSON.parse(data);
      
      // Check if it's a field reorder
      if (parsed.type === 'field' && parsed.index !== undefined && parsed.index !== targetIndex) {
        const newFields = [...fields];
        const [movedField] = newFields.splice(parsed.index, 1);
        // Adjust target index if source was before target
        const adjustedIndex = parsed.index < targetIndex ? targetIndex - 1 : targetIndex;
        newFields.splice(adjustedIndex, 0, movedField);
        // Update order
        newFields.forEach((field, idx) => {
          field.order = idx;
        });
        setFields(newFields);
      }
    } catch (error) {
      console.error('Error reordering field:', error);
    }
    setDragOverIndex(null);
    setDraggedFieldIndex(null);
  };

  if (isLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>
          {t('common.loading')}...
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '20px', padding: '20px', maxWidth: '1600px', margin: '0 auto' }}>
      {/* Question Bank Sidebar */}
      <QuestionBankSidebar onQuestionDrop={handleAddFromQuestionBank} />
      
      {/* Main Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
       <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
         <h1 style={{ fontSize: '24px', marginBottom: 0 }}>
           {formId ? t('formBuilder.editForm') : t('formBuilder.createNewForm')}
         </h1>
         <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
           <button
             onClick={() => setShowSettings(!showSettings)}
             title={showSettings ? t('formBuilder.closeSettings') || 'Close Settings' : t('formBuilder.openSettings') || 'Open Settings'}
             style={{
               background: showSettings ? '#007acc' : 'transparent',
               color: showSettings ? 'white' : '#666',
               border: 'none',
               padding: '8px',
               borderRadius: '6px',
               cursor: 'pointer',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               width: '36px',
               height: '36px',
               transition: 'all 0.2s ease',
               boxShadow: showSettings ? '0 2px 4px rgba(0,122,204,0.2)' : 'none'
             }}
             onMouseEnter={(e) => {
               if (!showSettings) {
                 e.currentTarget.style.background = '#f5f5f5';
                 e.currentTarget.style.color = '#333';
               } else {
                 e.currentTarget.style.background = '#005fa3';
               }
             }}
             onMouseLeave={(e) => {
               if (!showSettings) {
                 e.currentTarget.style.background = 'transparent';
                 e.currentTarget.style.color = '#666';
               } else {
                 e.currentTarget.style.background = '#007acc';
               }
             }}
           >
             <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
               <circle cx="12" cy="12" r="3"></circle>
               <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
             </svg>
           </button>
           <button
             onClick={() => navigate(`/${i18n.language}/dashboard?tab=forms`)}
             title={t('formBuilder.backToForms') || 'Back to Forms'}
             style={{
               background: 'transparent',
               color: '#666',
               border: 'none',
               padding: '8px',
               borderRadius: '6px',
               cursor: 'pointer',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               width: '36px',
               height: '36px',
               transition: 'all 0.2s ease'
             }}
             onMouseEnter={(e) => {
               e.currentTarget.style.background = '#f5f5f5';
               e.currentTarget.style.color = '#333';
             }}
             onMouseLeave={(e) => {
               e.currentTarget.style.background = 'transparent';
               e.currentTarget.style.color = '#666';
             }}
           >
             <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
               <path d="M19 12H5M12 19l-7-7 7-7"/>
             </svg>
           </button>
         </div>
       </div>

       {/* Form Settings - Collapsible */}
       {showSettings && (
         <div style={{ 
           background: 'white', 
           border: '1px solid #e9ecef', 
           borderRadius: '8px', 
           padding: '24px',
           marginBottom: '24px'
         }}>
           <h2 style={{ fontSize: '18px', marginBottom: '20px' }}>
             {t('formBuilder.formSettings')}
           </h2>

           {/* Form Name */}
           <div style={{ marginBottom: '16px' }}>
             <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
               {t('formBuilder.formName')} *
             </label>
             <input
               type="text"
               value={formName}
               onChange={(e) => setFormName(e.target.value)}
               placeholder={t('formBuilder.enterFormName')}
               style={{
                 width: '100%',
                 padding: '10px 12px',
                 border: '1px solid #ced4da',
                 borderRadius: '6px',
                 fontSize: '14px'
               }}
             />
           </div>

           {/* Form Description */}
           <div style={{ marginBottom: '16px' }}>
             <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
               {t('formBuilder.formDescription')}
             </label>
             <textarea
               value={formDescription}
               onChange={(e) => setFormDescription(e.target.value)}
               placeholder={t('formBuilder.enterDescription')}
               rows={3}
               style={{
                 width: '100%',
                 padding: '10px 12px',
                 border: '1px solid #ced4da',
                 borderRadius: '6px',
                 fontSize: '14px',
                 resize: 'vertical'
               }}
             />
           </div>

           {/* Access Control */}
           <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e9ecef' }}>
             <h3 style={{ fontSize: '16px', marginBottom: '16px', fontWeight: '600' }}>
               {t('formBuilder.accessControl') || 'Access Control'}
             </h3>

             {/* Shared With */}
             <div style={{ marginBottom: '16px' }}>
               <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
                 {t('formBuilder.sharedWith') || 'Share With'} *
               </label>
               <select
                 value={sharedWith}
                 onChange={(e) => {
                   const newValue = e.target.value as 'all' | 'specific';
                   setSharedWith(newValue);
                   // If "all" is selected, check all roles
                   if (newValue === 'all') {
                     setAllowedRoles(['admin', 'department_manager', 'program_manager', 'team_manager', 'instructor', 'social_worker']);
                   }
                 }}
                 style={{
                   width: '100%',
                   padding: '10px 12px',
                   border: '1px solid #ced4da',
                   borderRadius: '6px',
                   fontSize: '14px'
                 }}
               >
                 <option value="all">{t('formBuilder.shareAll') || 'All Users'}</option>
                 <option value="specific">{t('formBuilder.shareSpecific') || 'Specific Roles'}</option>
               </select>
             </div>

             {/* Allowed Roles - Always visible */}
             <div style={{ marginBottom: '16px' }}>
               <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
                 {t('formBuilder.allowedRoles') || 'Allowed Roles'} {sharedWith === 'specific' && '*'}
               </label>
               <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                 {['department_manager', 'program_manager', 'team_manager', 'instructor', 'social_worker'].map((role) => (
                   <label key={role} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                     <input
                       type="checkbox"
                       checked={allowedRoles.includes(role)}
                       disabled={sharedWith === 'all'}
                       onChange={(e) => {
                         if (e.target.checked) {
                           setAllowedRoles([...allowedRoles, role]);
                         } else {
                           setAllowedRoles(allowedRoles.filter(r => r !== role));
                         }
                       }}
                       style={{ 
                         width: '18px', 
                         height: '18px', 
                         cursor: sharedWith === 'all' ? 'not-allowed' : 'pointer',
                         opacity: sharedWith === 'all' ? 0.6 : 1
                       }}
                     />
                     <span style={{ opacity: sharedWith === 'all' ? 0.7 : 1 }}>{t(`users.roles.${role}`) || role}</span>
                   </label>
                 ))}
               </div>
               {sharedWith === 'specific' && allowedRoles.length === 0 && (
                 <small style={{ color: '#dc3545', display: 'block', marginTop: '4px' }}>
                   {t('formBuilder.selectAtLeastOneRole') || 'Please select at least one role'}
                 </small>
               )}
             </div>
           </div>
         </div>
       )}

      {/* Form Fields */}
      <div style={{ 
        background: 'white', 
        border: '1px solid #e9ecef', 
        borderRadius: '8px', 
        padding: '24px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <h2 style={{ fontSize: '18px', margin: 0 }}>
            {t('formBuilder.formFields')} ({fields.length})
          </h2>
          <button
            onClick={handleAddField}
            style={{
              background: '#007acc',
              color: 'white',
              border: 'none',
              padding: '10px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            + {t('formBuilder.addField')}
          </button>
        </div>

        {fields.length === 0 ? (
          <div 
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e)}
            style={{ 
              textAlign: 'center', 
              padding: '60px 40px', 
              color: '#666',
              border: '2px dashed #ced4da',
              borderRadius: '8px',
              background: '#fafafa',
              transition: 'all 0.2s ease'
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = '#007acc';
              e.currentTarget.style.background = '#e3f2fd';
            }}
            onDragLeave={(e) => {
              e.currentTarget.style.borderColor = '#ced4da';
              e.currentTarget.style.background = '#fafafa';
            }}
          >
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
            <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>{t('formBuilder.noFieldsYet')}</p>
            <p style={{ fontSize: '14px', color: '#999' }}>{t('formBuilder.dragFromBank') || 'Drag questions from the bank on the right'}</p>
          </div>
        ) : (
          <div 
            onDragOver={handleDragOver}
            onDrop={(e) => {
              // If dropping on the container (not on a field), add to end
              if (e.currentTarget === e.target || (e.target as HTMLElement).closest('.field-item') === null) {
                handleDrop(e, fields.length);
              }
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            {fields.map((field, index) => (
              <React.Fragment key={field.id}>
                {/* Drop zone before field */}
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.dataTransfer.dropEffect = 'copy';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDrop(e, index);
                  }}
                  style={{
                    height: dragOverIndex === index ? '8px' : '4px',
                    background: dragOverIndex === index ? '#007acc' : 'transparent',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    marginTop: index === 0 ? '0' : '-4px',
                    marginBottom: '-4px'
                  }}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setDragOverIndex(index);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    if (dragOverIndex === index) {
                      setDragOverIndex(null);
                    }
                  }}
                />
                <div
                  className="field-item"
                  draggable
                  onDragStart={(e) => {
                    e.stopPropagation();
                    handleFieldDragStart(e, index);
                  }}
                  onDragEnd={(e) => {
                    e.stopPropagation();
                    handleFieldDragEnd(e);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    const midpoint = rect.top + rect.height / 2;
                    // Determine if we're in the top or bottom half
                    if (e.clientY < midpoint) {
                      setDragOverIndex(index);
                    } else {
                      setDragOverIndex(index + 1);
                    }
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    const midpoint = rect.top + rect.height / 2;
                    // Check if it's a field reorder or a new question
                    try {
                      const data = e.dataTransfer.getData('text/plain');
                      const parsed = JSON.parse(data);
                      if (parsed.type === 'field') {
                        handleFieldDrop(e, e.clientY < midpoint ? index : index + 1);
                      } else {
                        // It's a question from the bank
                        handleDrop(e, e.clientY < midpoint ? index : index + 1);
                      }
                    } catch (error) {
                      // Fallback: treat as new question
                      handleDrop(e, index);
                    }
                    setDragOverIndex(null);
                  }}
                  onClick={() => handleEditField(field)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    background: '#fafafa',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: draggedFieldIndex === index ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (draggedFieldIndex !== index) {
                      e.currentTarget.style.borderColor = '#007acc';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,122,204,0.1)';
                      e.currentTarget.style.background = '#f0f8ff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (draggedFieldIndex !== index && dragOverIndex !== index) {
                      e.currentTarget.style.borderColor = '#e9ecef';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.background = '#fafafa';
                    }
                  }}
                >
                {/* Drag Handle */}
                <div 
                  style={{ 
                    cursor: 'grab',
                    color: '#999',
                    fontSize: '20px',
                    padding: '4px',
                    userSelect: 'none'
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  ⋮⋮
                </div>

                {/* Field info */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                    {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
                  </div>
                  <div style={{ fontSize: '13px', color: '#666' }}>
                    {t('formBuilder.fieldName')}: {field.field_name} | {t('formBuilder.type')}: {field.type}
                    {field.options && field.options.length > 0 && ` | ${field.options.length} ${t('formBuilder.options')}`}
                  </div>
                </div>

                {/* Delete button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteField(field.id);
                  }}
                  title={t('common.delete')}
                  style={{
                    background: 'transparent',
                    color: '#666',
                    border: '1px solid #ddd',
                    padding: '8px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#999';
                    e.currentTarget.style.color = '#333';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = '#ddd';
                    e.currentTarget.style.color = '#666';
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              </div>
              </React.Fragment>
            ))}
            {/* Drop zone after last field */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.dataTransfer.dropEffect = 'copy';
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDrop(e, fields.length);
              }}
              style={{
                height: dragOverIndex === fields.length ? '8px' : '4px',
                background: dragOverIndex === fields.length ? '#007acc' : 'transparent',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                marginTop: '-4px'
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                setDragOverIndex(fields.length);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                if (dragOverIndex === fields.length) {
                  setDragOverIndex(null);
                }
              }}
            />
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <button
          onClick={() => navigate('/dashboard?tab=forms')}
          style={{
            background: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={handleSaveForm}
          disabled={isSaving}
          style={{
            background: isSaving ? '#6c757d' : '#28a745',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {isSaving ? t('common.saving') : (formId ? t('common.update') : t('common.save'))}
        </button>
      </div>

    </div>

    {/* Field Editor Modal */}
      {showFieldEditor && editingField && (
        <FieldEditorModal
          field={editingField}
          onSave={handleSaveField}
          onCancel={() => {
            setShowFieldEditor(false);
            setEditingField(null);
          }}
          onChange={setEditingField}
        />
      )}
    </div>
  );
}

// Field Editor Modal Component
interface FieldEditorModalProps {
  field: FormFieldDefinition;
  onSave: () => void;
  onCancel: () => void;
  onChange: (field: FormFieldDefinition) => void;
}

function FieldEditorModal({ field, onSave, onCancel, onChange }: FieldEditorModalProps) {
  const { t } = useTranslation();
  const [optionsText, setOptionsText] = useState(field.options?.join('\n') || '');

  const fieldTypes: { value: FormFieldType; label: string }[] = [
    { value: 'text', label: t('formBuilder.fieldTypes.text') },
    { value: 'textarea', label: t('formBuilder.fieldTypes.textarea') },
    { value: 'number', label: t('formBuilder.fieldTypes.number') },
    { value: 'select', label: t('formBuilder.fieldTypes.select') },
    { value: 'multiselect', label: t('formBuilder.fieldTypes.multiselect') },
    { value: 'radio', label: t('formBuilder.fieldTypes.radio') },
    { value: 'checkbox', label: t('formBuilder.fieldTypes.checkbox') },
    { value: 'date', label: t('formBuilder.fieldTypes.date') },
    { value: 'email', label: t('formBuilder.fieldTypes.email') },
    { value: 'phone', label: t('formBuilder.fieldTypes.phone') },
    { value: 'scale', label: t('formBuilder.fieldTypes.scale') },
    { value: 'yes_no', label: t('formBuilder.fieldTypes.yesNo') }
  ];

  const needsOptions = ['select', 'multiselect', 'radio'].includes(field.type);

  const handleUpdate = (updates: Partial<FormFieldDefinition>) => {
    // If label is being updated, auto-generate field_name from it
    if (updates.label !== undefined) {
      // Convert label to field_name: lowercase, replace spaces with underscores, remove special chars
      const fieldName = updates.label
        .toLowerCase()
        .replace(/[^\w\s]/g, '') // Remove special characters
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .replace(/_+/g, '_') // Replace multiple underscores with single
        .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
      onChange({ ...field, ...updates, field_name: fieldName || field.field_name });
    } else {
      onChange({ ...field, ...updates });
    }
  };

  const handleSave = () => {
    // Parse options from textarea
    if (needsOptions && optionsText.trim()) {
      const options = optionsText.split('\n').filter(opt => opt.trim());
      handleUpdate({ options });
    }
    onSave();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        position: 'relative',
        padding: '30px'
      }}>
        <h3 style={{ marginBottom: '20px', fontSize: '20px' }}>
          {t('formBuilder.editField')}
        </h3>

        {/* Field Label (auto-generates field_name) */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
            {t('formBuilder.fieldLabel')} *
          </label>
          <input
            type="text"
            value={field.label}
            onChange={(e) => handleUpdate({ label: e.target.value })}
            placeholder={t('formBuilder.enterFieldLabel')}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #ced4da',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        {/* Field Type */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
            {t('formBuilder.fieldType')} *
          </label>
          <select
            value={field.type}
            onChange={(e) => handleUpdate({ type: e.target.value as FormFieldType })}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #ced4da',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            {fieldTypes.map(ft => (
              <option key={ft.value} value={ft.value}>{ft.label}</option>
            ))}
          </select>
        </div>

        {/* Options (for select, multiselect, radio) */}
        {needsOptions && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              {t('formBuilder.options')} * <small style={{ color: '#666' }}>({t('formBuilder.onePerLine')})</small>
            </label>
            <textarea
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              placeholder={t('formBuilder.enterOptions')}
              rows={5}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ced4da',
                borderRadius: '6px',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'monospace'
              }}
            />
          </div>
        )}

        {/* Description/Help Text */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
            {t('formBuilder.helpText')}
          </label>
          <input
            type="text"
            value={field.description || ''}
            onChange={(e) => handleUpdate({ description: e.target.value })}
            placeholder={t('formBuilder.enterHelpText')}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #ced4da',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        {/* Required checkbox */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={field.required || false}
              onChange={(e) => handleUpdate({ required: e.target.checked })}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ fontWeight: '500' }}>{t('formBuilder.requiredField')}</span>
          </label>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '20px', borderTop: '1px solid #e9ecef' }}>
          <button
            onClick={onCancel}
            style={{
              background: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            style={{
              background: '#28a745',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FormBuilder;

